<script setup>
import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import router from "@/router";
import useValidation from "@/utils/helpers/validations";
import AuthLayout from "@/views/layouts/AuthLayout.vue";
import AuthHeroPanel from "@/components/auth/AuthHeroPanel.vue";
import { resolveLandingLink } from "@/components/sideMenuItems.js";

const { checkEmailValidation } = useValidation();

const authStore = useAuthStore();
const email = ref(null);
const password = ref(null);
const showPassword = ref(false);
let isValidEmail = ref(true);
let isLoginError = ref(false);
let isSubmitted = ref(false);

const validEmail = () => {
  isLoginError.value = false;
  isValidEmail.value = checkEmailValidation(email.value);
};

const onSubmit = async () => {
  if (isSubmitted.value) return; // guard: browsers can fire click + keyup.enter simultaneously
  isLoginError.value = false;
  isSubmitted.value = true;
  const data = { email: email.value, password: password.value };
  authStore
    .login(data)
    .then(async () => {
      const userDetails = JSON.parse(localStorage.getItem("user"));
      if (
        (userDetails && userDetails.user?.email == process.env.VUE_APP_SUPER_ADMIN_EMAIL) ||
        (userDetails && userDetails.user?.companies && userDetails.user.companies.length > 1)
      ) {
        router.push("/companies");
      } else {
        router.push(resolveLandingLink(userDetails, process.env.VUE_APP_SUPER_ADMIN_EMAIL));
      }
    })
    .catch(() => {
      isLoginError.value = true;
      isSubmitted.value = false;
    });
};
</script>

<template>
  <AuthLayout variant="split">
    <div class="login-split" @keyup.enter="!(!email || !password || !isValidEmail || isSubmitted) && onSubmit()">
      <!-- Form panel -->
      <section class="login-form-panel">
        <div class="login-form-panel__inner">
          <div class="auth-logo-wrap login-brand">
            <img class="auth-logo-img" src="@/assets/images/logo/hiddenox.svg" alt="Ravo" />
            <span class="auth-logo-text">Ravo</span>
          </div>

          <div class="login-head">
            <h1 class="login-head__title">Welcome back</h1>
            <p class="login-head__subtitle">Sign in to manage your agents, devices, and conversations.</p>
          </div>

          <!-- Email -->
          <div class="auth-field-wrap">
            <span class="p-float-label auth-float">
              <PInput
                id="login-email"
                :model-value="email"
                @input="email = $event.target.value; validEmail()"
                type="email"
                :class="{ 'p-invalid': !isValidEmail || isLoginError }"
                class="auth-pinput"
                autocomplete="email"
              />
              <label for="login-email">Email address</label>
            </span>
            <small v-if="!isValidEmail" class="auth-field-error">Invalid email address.</small>
          </div>

          <!-- Password -->
          <div class="auth-field-wrap auth-field-wrap--password">
            <span class="p-float-label auth-float">
              <PInput
                id="login-password"
                :model-value="password"
                @input="password = $event.target.value; isLoginError = false"
                :type="showPassword ? 'text' : 'password'"
                :class="{ 'p-invalid': isLoginError }"
                class="auth-pinput"
                autocomplete="current-password"
              />
              <label for="login-password">Password</label>
            </span>
            <button type="button" class="auth-eye-btn" @click="showPassword = !showPassword" tabindex="-1">
              <i :class="showPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
            </button>
          </div>

          <p v-if="isLoginError" class="auth-error-msg">Login credentials are incorrect.</p>

          <button
            @click="onSubmit"
            :disabled="!email || !password || !isValidEmail || isSubmitted"
            type="button"
            class="auth-submit-btn"
          >
            LOGIN
            <span v-if="isSubmitted" class="spinner-sm" role="status" aria-hidden="true"></span>
          </button>

          <p class="login-foot">
            New to Ravo? <router-link class="login-foot__link" to="/register">Create an account</router-link>
          </p>
        </div>
      </section>

      <!-- Brand hero panel -->
      <AuthHeroPanel
        tag="AI Operations Console"
        title-line="Your agents,"
        title-emphasis="always on."
        description="Ravo brings every AI agent, kiosk, and conversation into one calm control room — so you can watch, tune, and ship in real time."
        :features="[
          'Live agent voice & video testing',
          'Multi-site device monitoring',
          'Campaigns & data sources in one place',
        ]"
      />
    </div>
  </AuthLayout>
</template>

<style scoped>
.login-split {
  min-height: 100vh;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
}

/* ── Form panel ─────────────────────────────────────────────────── */
.login-form-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  background: #f4f6fb;
  transition: background 0.3s;
}

.login-form-panel__inner {
  width: 100%;
  max-width: 384px;
}

.login-brand {
  justify-content: flex-start;
  margin-bottom: 40px;
}

.login-head {
  margin-bottom: 28px;
}

.login-head__title {
  margin: 0 0 8px;
  font-family: "lato-black", Lato, sans-serif;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
  text-wrap: balance;
}

.login-head__subtitle {
  margin: 0;
  font-family: Lato, sans-serif;
  font-size: 14.5px;
  color: #64748b;
  line-height: 1.55;
}

/* ── "New to Ravo?" footer link ──────────────────────────────────── */
.login-foot {
  margin: 20px 0 0;
  text-align: center;
  font-family: Lato, sans-serif;
  font-size: 13.5px;
  color: #64748b;
}

.login-foot__link {
  color: var(--brand-orange-2, #da3200);
  font-weight: 700;
  text-decoration: none;
}

.login-foot__link:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* ── Centered submit button ────────────────────────────────────── */
.login-form-panel .auth-submit-btn {
  width: auto;
  min-width: 200px;
  margin-left: auto;
  margin-right: auto;
}

/* ── Eye toggle flush to the input's end ─────────────────────────── */
.login-form-panel .auth-eye-btn {
  right: 4px;
}

/* ── Dark mode ──────────────────────────────────────────────────── */
[data-theme="dark"] .login-form-panel {
  background: #0d1117;
}

[data-theme="dark"] .login-head__title {
  color: #e8edf5;
}

[data-theme="dark"] .login-head__subtitle {
  color: #9aadc4;
}

[data-theme="dark"] .login-foot {
  color: #9aadc4;
}

[data-theme="dark"] .login-foot .login-foot__link {
  color: var(--brand-orange-2, #da3200) !important;
}

/* ── Responsive: hero collapses, form goes single column ─────────── */
@media (max-width: 900px) {
  .login-split {
    grid-template-columns: 1fr;
  }

  .login-form-panel {
    padding: 40px 22px;
  }
}
</style>
